import React, { useEffect, useState, useCallback } from "react";
import { warehousesAPI } from "../../services/api";
import toast from "react-hot-toast";
import { Plus, ArrowRight, ArrowLeft } from "lucide-react";
import { monthLabel } from "../../utils/month";
import { localDateStr } from "../../utils/date";

interface WarehouseRef { _id: string; name: string; }
interface Transfer {
  _id: string;
  fromWarehouseId: WarehouseRef;
  toWarehouseId: WarehouseRef;
  name: string; unit: string; quantity: number; date: string; notes: string;
}

const INPUT = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-400";

function toDateInput(iso?: string) {
  if (!iso) return localDateStr();
  return new Date(iso).toISOString().slice(0, 10);
}
function formatDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function TransferPanel({ warehouseId, month, allWarehouses }: {
  warehouseId: string; month: string; allWarehouses: WarehouseRef[];
}) {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading]     = useState(false);
  const [adding, setAdding]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const otherWarehouses = allWarehouses.filter(w => w._id !== warehouseId);
  const [form, setForm] = useState({
    toWarehouseId: otherWarehouses[0]?._id || "",
    name: "", unit: "", quantity: "1", date: toDateInput(), notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getTransfers(warehouseId, month); setTransfers(res.data.data); }
    catch { toast.error("Lỗi tải chuyển kho"); }
    finally { setLoading(false); }
  }, [warehouseId, month]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ toWarehouseId: otherWarehouses[0]?._id || "", name: "", unit: "", quantity: "1", date: toDateInput(), notes: "" });
    setAdding(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nhập tên hàng"); return; }
    if (!form.toWarehouseId) { toast.error("Chọn kho đến"); return; }
    setSaving(true);
    try {
      await warehousesAPI.createTransfer(warehouseId, {
        fromWarehouseId: warehouseId,
        toWarehouseId: form.toWarehouseId,
        name: form.name, unit: form.unit, quantity: Number(form.quantity) || 1,
        date: form.date, notes: form.notes,
      });
      toast.success("Đã ghi nhận chuyển kho");
      setAdding(false);
      load();
    } catch { toast.error("Lỗi lưu"); } finally { setSaving(false); }
  };

  const del = async (t: Transfer) => {
    if (!window.confirm(`Xoá phiếu chuyển "${t.name}"?`)) return;
    try { await warehousesAPI.deleteTransfer(warehouseId, t._id); toast.success("Đã xoá"); load(); }
    catch { toast.error("Lỗi xoá"); }
  };

  if (otherWarehouses.length === 0) {
    return <p className="text-sm text-zinc-400 py-6 text-center">Cần có ít nhất 2 kho để chuyển hàng qua lại.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 text-sm text-amber-600 font-medium py-1 touch-manipulation">
          <Plus size={14} /> Ghi nhận chuyển kho
        </button>
      </div>

      {adding && (
        <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Chuyển đến kho *</label>
            <select className={INPUT + " bg-white"} value={form.toWarehouseId}
              onChange={e => setForm(f => ({ ...f, toWarehouseId: e.target.value }))}>
              {otherWarehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
            </select>
          </div>
          <input className={INPUT} placeholder="Tên hàng *" value={form.name} autoFocus
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <input className={INPUT} placeholder="ĐVT (cái, hộp...)" value={form.unit}
              onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
            <input type="number" inputMode="numeric" min={0} className={INPUT} placeholder="Số lượng" value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
          </div>
          <input type="date" className={INPUT} value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          <input className={INPUT} placeholder="Ghi chú (tuỳ chọn)" value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving}
              className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50 touch-manipulation">
              {saving ? "Đang lưu..." : "Ghi nhận"}
            </button>
            <button onClick={() => setAdding(false)}
              className="px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600 touch-manipulation">Huỷ</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-zinc-400 text-center py-3">Đang tải...</p>
      ) : transfers.length === 0 ? (
        <p className="text-sm text-zinc-400 text-center py-6">Chưa có phiếu chuyển kho trong {monthLabel(month)}</p>
      ) : (
        <div className="space-y-2">
          {transfers.map(t => {
            const isOut = t.fromWarehouseId._id === warehouseId;
            const counterpart = isOut ? t.toWarehouseId?.name : t.fromWarehouseId?.name;
            return (
              <div key={t._id} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3">
                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0
                  ${isOut ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                  {isOut ? <ArrowRight size={12} /> : <ArrowLeft size={12} />}
                  {isOut ? "Chuyển đi" : "Chuyển đến"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-800">{t.name} {t.unit && <span className="text-zinc-400 font-normal">({t.unit})</span>}</p>
                  <p className="text-xs text-zinc-400">
                    {isOut ? "Đến" : "Từ"} <span className="font-medium text-zinc-500">{counterpart}</span> · {formatDate(t.date)}
                    {t.notes && <> · {t.notes}</>}
                  </p>
                </div>
                <span className="text-sm font-bold text-zinc-700 flex-shrink-0">{t.quantity}</span>
                <button onClick={() => del(t)} className="text-xs text-zinc-300 hover:text-red-500 flex-shrink-0">Xoá</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
